import { useEffect, useState } from "react";
import { Plus, Trash2, TrendingUp, TrendingDown, Minus, RefreshCw, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Keywords() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ keyword: "", target_url: "", position: "", location: "" });
  const [checking, setChecking] = useState({});
  const [checkingAll, setCheckingAll] = useState(false);

  const load = () => api.get("/keywords").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.keyword) { toast.error("Mot-clé requis"); return; }
    await api.post("/keywords", { ...form, position: form.position ? Number(form.position) : null });
    setForm({ keyword: "", target_url: "", position: "", location: "" });
    load();
  };

  const remove = async (id) => { await api.delete(`/keywords/${id}`); load(); };

  const checkOne = async (id) => {
    setChecking((c) => ({ ...c, [id]: true }));
    try {
      const { data } = await api.post(`/keywords/${id}/check`);
      setItems((its) => its.map((k) => (k.id === id ? data : k)));
      toast.success(data.position ? `Position #${data.position}` : "Hors du top 100");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la vérification"); }
    finally { setChecking((c) => ({ ...c, [id]: false })); }
  };

  const checkAll = async () => {
    setCheckingAll(true);
    for (const k of items) {
      try { const { data } = await api.post(`/keywords/${k.id}/check`); setItems((its) => its.map((x) => (x.id === k.id ? data : x))); } catch { /* skip */ }
    }
    setCheckingAll(false);
    toast.success("Positions mises à jour");
  };

  const trend = (h) => {
    const pts = (h || []).filter((x) => x.position);
    if (pts.length < 2) return { icon: Minus, color: "text-stone" };
    const diff = pts[pts.length - 2].position - pts[pts.length - 1].position;
    if (diff > 0) return { icon: TrendingUp, color: "text-green-600" };
    if (diff < 0) return { icon: TrendingDown, color: "text-red-500" };
    return { icon: Minus, color: "text-stone" };
  };

  const input = "rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl text-forest mb-1">Suivi de positionnement</h1>
          <p className="text-stone">Positions Google réelles via SerpAPI (google.fr)</p>
        </div>
        <button onClick={checkAll} disabled={checkingAll || items.length === 0} data-testid="check-all-btn"
          className="rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-brand transition-colors flex items-center gap-2 disabled:opacity-60">
          {checkingAll ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Vérifier toutes les positions
        </button>
      </div>

      <div className="rounded-xl bg-white border border-border p-5 mb-6 grid sm:grid-cols-6 gap-3" data-testid="keyword-form">
        <input className={`${input} sm:col-span-2`} placeholder="Mot-clé (ex: paysagiste Aix)" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} data-testid="kw-keyword" />
        <input className={input} placeholder="Domaine cible (optionnel)" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} data-testid="kw-domain" />
        <input className={input} placeholder="Ville / zone" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <input className={input} type="number" placeholder="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="kw-position" />
        <button onClick={add} data-testid="kw-add" className="rounded-lg bg-brand text-cream text-sm font-semibold flex items-center justify-center gap-1 hover:bg-forest"><Plus size={16} /> Ajouter</button>
      </div>

      <div className="rounded-xl bg-white border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-stone text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Mot-clé</th><th className="text-left px-4 py-3 hidden sm:table-cell">Zone</th><th className="px-4 py-3">Position</th><th className="px-4 py-3">Tendance</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((k) => {
              const T = trend(k.history);
              return (
                <tr key={k.id} data-testid={`kw-row-${k.id}`}>
                  <td className="px-5 py-3 font-medium text-forest">{k.keyword}</td>
                  <td className="px-4 py-3 text-stone hidden sm:table-cell">{k.location || "—"}</td>
                  <td className="px-4 py-3 text-center font-semibold text-forest" data-testid={`kw-pos-${k.id}`}>{k.position ? `#${k.position}` : "—"}</td>
                  <td className="px-4 py-3 text-center"><T.icon className={`inline ${T.color}`} size={18} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => checkOne(k.id)} disabled={checking[k.id]} data-testid={`kw-check-${k.id}`} className="text-brand hover:bg-brand/5 rounded p-1.5 mr-1" title="Vérifier la position">
                      {checking[k.id] ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
                    </button>
                    <button onClick={() => remove(k.id)} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-stone py-8">Aucun mot-clé suivi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
